import { createSelector } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { ProjectType } from './models';
import { baseApi } from '@centerpiece/components';

export const project_type = baseApi
  .enhanceEndpoints({ addTagTypes: ['ProjectTypes', 'ProjectType'] })
  .injectEndpoints({
    endpoints: builder => ({
      getProjectTypes: builder.query<ProjectType[], { params?: object }>({
        query: ({ params }: { params?: object }) => ({
          url: 'project_type',
          method: 'GET',
          params: params,
        }),
        providesTags: (result, error, arg) =>
          result
            ? [
                ...result.map(({ id }) => ({
                  type: 'ProjectType' as const,
                  id,
                })),
                'ProjectTypes',
              ]
            : ['ProjectTypes'],
      }),
      getProjectType: builder.query<ProjectType, string>({
        query: id => ({
          url: `project_type/${id}`,
          method: 'GET',
        }),
        providesTags: (result, error, arg) => [
          { type: 'ProjectType', id: arg },
        ],
        // configuration for an individual endpoint, overriding the api setting
        keepUnusedDataFor: 90,
      }),
      createProjectType: builder.mutation({
        query: request => ({
          url: 'project_type',
          method: 'POST',
          body: request,
        }),
        invalidatesTags: ['ProjectTypes'],
      }),
      updateProjectType: builder.mutation<ProjectType, ProjectType>({
        query: request => ({
          url: `project_type/${request.id}`,
          method: 'PUT',
          body: request,
        }),
        invalidatesTags: (result, error, arg) => [
          { type: 'ProjectType', id: arg.id },
        ],
        async onQueryStarted({ id, ...patch }, { dispatch, queryFulfilled }) {
          const patchResult = dispatch(
            project_type.util.updateQueryData(
              'getProjectType',
              `${id}`,
              draft => {
                Object.assign(draft, patch);
              }
            )
          );
          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();

            /**
             * Alternatively, on failure you can invalidate the corresponding cache tags
             * to trigger a re-fetch:
             * dispatch(project_type.util.invalidateTags(['ProjectTypes']))
             */
            dispatch(project_type.util.invalidateTags(['ProjectTypes']));
          }
        },
      }),
      deleteProjectType: builder.mutation<ProjectType, number>({
        query: id => ({
          url: `project_type/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (result, error, arg) =>
          result
            ? [{ type: 'ProjectType' as const, arg }, 'ProjectTypes']
            : ['ProjectTypes'],
      }),
    }),
  });

// Auto-generated hooks
export const {
  useGetProjectTypesQuery,
  useGetProjectTypeQuery,
  useCreateProjectTypeMutation,
  useUpdateProjectTypeMutation,
  useDeleteProjectTypeMutation,
  usePrefetch: useProjectTypesPrefetch,
} = project_type;
const selectProjectTypesRoot = project_type.endpoints.getProjectTypes.select(
  {}
);
export const selectProjectTypes = createSelector(
  selectProjectTypesRoot,
  project_type => project_type.data || []
);
